import React from 'react';
import { Grid, InputLabel, OutlinedInput } from '@mui/material';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';
import DefaultUploadFile from '../../../../components/default-upload-file';
import { FieldError, FormSectionTitle } from '../../../../components/form-page-parts';
import { ValidationErrors } from '../../../../utils/validation';
import { LookupSelect, toLookupOptions } from '../../shared/lookup-select';
import { useContractCreateEditState } from '../context/contract-create-edit-context';
import { ContractField } from '../context/contract-validation';

const MONEY_FIELDS: { name: string; label: string; field: 'initialValue' | 'paymentInstallmentlValue' | 'globalValue' | 'accumulatedValue'; required: boolean }[] = [
    { name: 'input-initial-value', label: 'Valor Inicial', field: 'initialValue', required: true },
    { name: 'input-payment-installment-value', label: 'Valor Parcela', field: 'paymentInstallmentlValue', required: false },
    { name: 'input-global-value', label: 'Valor Global', field: 'globalValue', required: true },
    { name: 'input-acumulated-value', label: 'Valor Acumulado', field: 'accumulatedValue', required: true },
];

// Valores, objeto/observações e, na inclusão, o documento publicado junto com o contrato
export function ContractValuesSection({ errors, onChange, onFileChange }: {
    errors: ValidationErrors<ContractField>; onChange: (e: any) => void; onFileChange: (file: File) => void;
}) {
    const { contractRequest, contractId, typeDocument, file } = useContractCreateEditState();
    const isEdit = contractId !== undefined;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <FormSectionTitle title="Valores Financeiros" />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <InputLabel required>Nº Parcelas</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" type="number" inputProps={{ min: 1, max: 1000 }} name="input-number-payment-installment" value={contractRequest.numberPaymentInstallment || ''} onChange={onChange} error={!!errors.numberPaymentInstallment} />
                <FieldError message={errors.numberPaymentInstallment} />
            </Grid>
            {MONEY_FIELDS.map(({ name, label, field, required }) => (
                <Grid item xs={12} sm={4} md={2} key={name}>
                    <InputLabel required={required}>{label}</InputLabel>
                    <BrazilianNumberInput
                        fullWidth
                        value={contractRequest[field] || 0}
                        onChange={(value) => onChange({ target: { name, value } })}
                        decimals={2}
                        prefix="R$"
                        placeholder="0,00"
                        error={!!errors[field as ContractField]}
                    />
                    <FieldError message={errors[field as ContractField]} />
                </Grid>
            ))}

            <Grid item xs={12}>
                <FormSectionTitle title="Descrição" />
            </Grid>
            <Grid item xs={12}>
                <InputLabel required>Objeto</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" multiline rows={5} name="input-object" value={contractRequest.object || ''} onChange={onChange} error={!!errors.object} />
                <FieldError message={errors.object} />
            </Grid>
            <Grid item xs={12}>
                <InputLabel>Mais Informações</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" multiline rows={4} name="input-notes" value={contractRequest.notes || ''} onChange={onChange} />
            </Grid>

            {!isEdit && (
                <React.Fragment>
                    <Grid item xs={12}>
                        <FormSectionTitle title="Documento" />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <LookupSelect required label="Tipo de Documento" name="select-type-document-id" value={contractRequest.typeDocumentId} options={toLookupOptions(typeDocument)} onChange={onChange} error={errors.typeDocumentId} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={8}>
                        <InputLabel required>Título do Documento</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="input-document-title" value={contractRequest.documentTitle || ''} onChange={onChange} error={!!errors.documentTitle} />
                        <FieldError message={errors.documentTitle} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Arquivo</InputLabel>
                        <DefaultUploadFile props={{ id: 'file-upload-contract', setFileUpload: onFileChange, file, extensions: ['pdf'], maxFileSizeMb: 30, error: !!errors.file }} />
                        <FieldError message={errors.file} />
                    </Grid>
                </React.Fragment>
            )}
        </React.Fragment>
    );
}
